import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTable, useSortBy } from 'react-table';
import usePendingTransactions from '../../hooks/transactions/usePendingTransactions';
import { loginData } from '../../stores/auth';
import './style/pendingTransactions.scss';

const PendingTransactions = () => {
  const dataLogin = useSelector(loginData);
  const { transactions, isLoading, error } = usePendingTransactions(
    dataLogin?.data?.token
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('');
  const [selectedOrderType, setSelectedOrderType] = useState('');

  const filteredData = useMemo(() => {
    return transactions.filter((transaction) => {
      const matchesSearch =
        (transaction.id?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (transaction.product?.toLowerCase() || '').includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === '' || transaction.category === selectedCategory;
      const matchesPaymentStatus =
        selectedPaymentStatus === '' ||
        transaction.paymentStatus === selectedPaymentStatus;
      const matchesOrderType =
        selectedOrderType === '' || transaction.orderType === selectedOrderType;

      return (
        matchesSearch &&
        matchesCategory &&
        matchesPaymentStatus &&
        matchesOrderType
      );
    });
  }, [transactions, searchTerm, selectedCategory, selectedPaymentStatus, selectedOrderType]);

  const data = useMemo(() => filteredData, [filteredData]);

  const columns = React.useMemo(
    () => [
      {
        Header: '',
        accessor: 'selection',
        Cell: () => (
          <input type="checkbox" className="row-checkbox" />
        ),
        width: 40,
      },
      {
        Header: 'Transaction ID',
        accessor: 'id',
        Cell: ({ row }) => (
          <Link to={`/transactions/pending/${row.original.id}`}>
            {row.original.id}
          </Link>
        ),
      },
      {
        Header: 'Product Name',
        accessor: 'product',
        Cell: ({ row }) => (
          <div className="product-cell">
            {/* <img src={row.original.image} alt="product" className="product-image" /> */}
            <span>{row.original.product}</span>
          </div>
        ),
      },
      {
        Header: 'Transaction Date',
        accessor: 'date',
      },
      {
        Header: 'Order Status',
        accessor: 'orderStatus',
      },
      {
        Header: 'Payment Status',
        accessor: 'paymentStatus',
      },
      {
        Header: 'Category',
        accessor: 'category',
      },
      {
        Header: 'Order Type',
        accessor: 'orderType',
      },
      {
        Header: 'Total Transaction',
        accessor: 'total',
      },
      {
        Header: 'Shipping By',
        accessor: 'shipping',
      },
    ],
    []
  );

  const tableInstance = useTable({ columns, data }, useSortBy);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = tableInstance;

  return (
    <div className="pending-transactions">
      {error && <div className="error-message">{error}</div>}

      <div className="search-section">
        <input
          type="text"
          placeholder="Search transaction id or product name"
          className="search-bar"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="filters">
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">All Category</option>
          {[...new Set(transactions.map((t) => t.category))].map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <select
          value={selectedPaymentStatus}
          onChange={(e) => setSelectedPaymentStatus(e.target.value)}
        >
          <option value="">All Payment Status</option>
          {[...new Set(transactions.map((t) => t.paymentStatus))].map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
        <select
          value={selectedOrderType}
          onChange={(e) => setSelectedOrderType(e.target.value)}
        >
          <option value="">All Order Type</option>
          {[...new Set(transactions.map((t) => t.orderType))].map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="loading">Loading transactions...</div>
      ) : (
        <div className="table-container">
          <table {...getTableProps()} className="transactions-table">
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                      {column.Header === '' ? (
                        <input type="checkbox" className="header-checkbox" />
                      ) : (
                        column.render('Header')
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.length > 0 ? (
                rows.map(row => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map(cell => (
                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '20px' }}>
                    No transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PendingTransactions;
